import React, { useRef, useEffect, useCallback } from 'react';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const SHORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const YEARS = Array.from({ length: 11 }, (_, i) => 2020 + i);
const ITEM_HEIGHT = 40;
const VISIBLE = 3;
const REPEATS = 3;

function InfiniteScrollColumn({ items, selected, onSelect, label, renderItem }) {
  const ref = useRef(null);
  const settling = useRef(false);
  const tripleItems = [...items, ...items, ...items];
  const baseOffset = items.length * ITEM_HEIGHT;

  useEffect(() => {
    if (!ref.current) return;
    const idx = items.indexOf(selected);
    if (idx >= 0) {
      ref.current.scrollTop = baseOffset + idx * ITEM_HEIGHT;
    }
  }, []);

  const snapToCenter = useCallback(() => {
    if (!ref.current) return;
    const st = ref.current.scrollTop;
    const idx = Math.round(st / ITEM_HEIGHT);
    const clamped = idx % items.length;
    const actual = clamped < 0 ? clamped + items.length : clamped;
    onSelect(items[actual]);

    // Re-center to middle band if scrolled too far
    if (st < ITEM_HEIGHT * 2 || st > baseOffset * 2 + ITEM_HEIGHT * (items.length - 2)) {
      settling.current = true;
      ref.current.scrollTop = baseOffset + actual * ITEM_HEIGHT;
      settling.current = false;
    }
  }, [items, onSelect, baseOffset]);

  const handleScroll = useCallback(() => {
    if (settling.current) return;
    clearTimeout(ref.current?._t);
    ref.current._t = setTimeout(snapToCenter, 60);
  }, [snapToCenter]);

  return (
    <div className="flex-1 flex flex-col items-center">
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60 mb-1.5 font-medium">{label}</span>
      <div className="relative overflow-hidden" style={{ height: VISIBLE * ITEM_HEIGHT }}>
        <div className="absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-background/95 to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-background/95 to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-x-0 z-10 pointer-events-none border-y border-primary/20 rounded-lg bg-primary/5" style={{ top: ITEM_HEIGHT, height: ITEM_HEIGHT }} />
        <div
          ref={ref}
          onScroll={handleScroll}
          className="h-full scrollbar-hide"
          style={{
            overflowY: 'auto',
            overflowX: 'hidden',
            scrollSnapType: 'y mandatory',
            paddingTop: ITEM_HEIGHT,
            paddingBottom: ITEM_HEIGHT,
            overscrollBehavior: 'contain',
            touchAction: 'pan-y',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {tripleItems.map((item, i) => {
            const isSelected = item === selected;
            return (
              <div
                key={`${item}-${i}`}
                style={{ height: ITEM_HEIGHT, scrollSnapAlign: 'start' }}
                className={`flex items-center justify-center text-sm transition-opacity ${
                  isSelected ? 'text-primary font-semibold' : 'text-muted-foreground/50'
                }`}
              >
                {renderItem ? renderItem(item) : item}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function ScrollDatePicker({ date, onDateChange, onClose }) {
  const d = date || new Date();
  const month = d.getMonth();
  const day = d.getDate();
  const year = d.getFullYear();

  const monthItems = Array.from({ length: 12 }, (_, i) => i);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const dayItems = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const setMonth = (m) => {
    const maxDay = new Date(year, m + 1, 0).getDate();
    onDateChange(new Date(year, m, Math.min(day, maxDay)));
  };
  const setDay = (dy) => onDateChange(new Date(year, month, dy));
  const setYear = (y) => {
    const maxDay = new Date(y, month + 1, 0).getDate();
    onDateChange(new Date(y, month, Math.min(day, maxDay)));
  };

  return (
    <div className="w-[270px] p-4" style={{ touchAction: 'pan-y' }}>
      <div className="flex space-x-2">
        <InfiniteScrollColumn
          items={monthItems}
          selected={month}
          onSelect={setMonth}
          label="Month"
          renderItem={(m) => SHORT_MONTHS[m]}
        />
        <InfiniteScrollColumn
          items={dayItems}
          selected={day}
          onSelect={setDay}
          label="Day"
        />
        <InfiniteScrollColumn
          items={YEARS}
          selected={year}
          onSelect={setYear}
          label="Year"
        />
      </div>
      <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-border/30">
        <button
          onClick={() => { onDateChange(null); onClose(); }}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          data-testid="clear-custom-date"
        >
          Reset to today
        </button>
        <button
          onClick={onClose}
          className="text-xs font-semibold text-primary hover:text-primary/80 transition-colors px-3 py-1 rounded-full bg-primary/10"
          data-testid="confirm-date-picker"
        >
          Done
        </button>
      </div>
    </div>
  );
}
